import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Popover, Tooltip } from "antd";
import {
    SettingOutlined,
    HolderOutlined,
    VerticalAlignTopOutlined,
    VerticalAlignMiddleOutlined,
    VerticalAlignBottomOutlined,
    LockOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import type { ColumnSettingItem, ColumnSettingProps, FixedSide } from "../../hooks/useTableColumnSettings";
import "./style.scss";

type RegisterRow = (key: string, el: HTMLElement | null) => void;

// Bỏ dấu tiếng Việt để tìm "ngay" vẫn ra "Ngày tạo"
const normalize = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").toLowerCase();

const labelText = (item: ColumnSettingItem) => (typeof item.label === "string" ? item.label : item.key);

/* ================= 1 DÒNG CỘT ĐANG HIỂN THỊ (kéo thả được) ================= */

function SortableRow({
    item,
    dragDisabled,
    onToggle,
    onFixedChange,
    registerRow,
}: {
    item: ColumnSettingItem;
    dragDisabled: boolean;
    onToggle: (key: string) => void;
    onFixedChange: (key: string, side?: FixedSide) => void;
    registerRow: RegisterRow;
}) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.key, disabled: dragDisabled });

    // Nút cố định: hiện 2 lựa chọn còn lại so với trạng thái hiện tại (giống ProTable)
    const allActions: { side?: FixedSide; icon: React.ReactNode; label: string }[] = [
        { side: "left", icon: <VerticalAlignTopOutlined />, label: t("table_setting.pin_left") },
        { side: undefined, icon: <VerticalAlignMiddleOutlined />, label: t("table_setting.unpin") },
        { side: "right", icon: <VerticalAlignBottomOutlined />, label: t("table_setting.pin_right") },
    ];
    const actions = allActions.filter((a) => a.side !== item.fixed);

    return (
        <div
            ref={(el) => { setNodeRef(el); registerRow(item.key, el); }}
            style={{ transform: CSS.Translate.toString(transform), transition }}
            className={`
                group flex items-center gap-2 h-10 px-2 rounded-md select-none
                hover:bg-gray-100 dark:hover:bg-[#2a2a2a]
                ${isDragging ? "relative z-10 bg-white dark:bg-[#2a2a2a] shadow-md" : ""}
            `}
        >
            <span
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                className={`flex items-center justify-center w-6 h-8 rounded text-gray-400 dark:text-gray-500 ${dragDisabled
                    ? "opacity-30 cursor-not-allowed"
                    : "cursor-grab active:cursor-grabbing hover:text-gray-600 dark:hover:text-gray-300"}`}
                aria-label="drag"
            >
                <HolderOutlined />
            </span>

            <Checkbox
                checked
                disabled={item.locked}
                onChange={() => onToggle(item.key)}
                className="flex-1 min-w-0 !text-[14px] text-000000e0 dark:!text-gray-100"
            >
                <span className="block truncate">{item.label}</span>
            </Checkbox>

            {item.locked && (
                <Tooltip title={t("table_setting.locked")}>
                    <LockOutlined className="text-gray-400 text-[12px]" />
                </Tooltip>
            )}

            <div className="flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
                {actions.map((a) => (
                    <Tooltip key={a.side || "none"} title={a.label}>
                        <button
                            type="button"
                            onClick={() => onFixedChange(item.key, a.side)}
                            className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:text-[#3e91ab] hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-[#383838]"
                        >
                            {a.icon}
                        </button>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
}

/* ================= 1 DÒNG CỘT ĐANG ẨN ================= */

function HiddenRow({
    item,
    onToggle,
    registerRow,
}: {
    item: ColumnSettingItem;
    onToggle: (key: string) => void;
    registerRow: RegisterRow;
}) {
    return (
        <div
            ref={(el) => registerRow(item.key, el)}
            className="flex items-center gap-2 h-10 px-2 rounded-md select-none hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
        >
            {/* Giữ chỗ bằng tay kéo để chữ thẳng hàng với nhóm đang hiển thị */}
            <span className="w-6 shrink-0" />
            <Checkbox
                checked={false}
                onChange={() => onToggle(item.key)}
                className="flex-1 min-w-0 !text-[14px] text-gray-500 dark:!text-gray-400"
            >
                <span className="block truncate">{item.label}</span>
            </Checkbox>
        </div>
    );
}

/* ================= 1 NHÓM CỐ ĐỊNH (trái / giữa / phải) ================= */

function ColumnGroup({
    title,
    items,
    onMove,
    ...rowProps
}: {
    title: string;
    items: ColumnSettingItem[];
    dragDisabled: boolean;
    onMove: (activeKey: string, overKey: string) => void;
    onToggle: (key: string) => void;
    onFixedChange: (key: string, side?: FixedSide) => void;
    registerRow: RegisterRow;
}) {
    // distance: tránh việc click checkbox / nút bị hiểu là kéo
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    if (!items.length) return null;

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (over && active.id !== over.id) onMove(String(active.id), String(over.id));
    };

    return (
        <div className="mb-2 last:mb-0">
            <div className="px-2 pt-1 pb-1 text-[11.5px] text-gray-400 dark:text-gray-500">{title}</div>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={items.map((i) => i.key)} strategy={verticalListSortingStrategy}>
                    <div>
                        {items.map((item) => (
                            <SortableRow key={item.key} item={item} {...rowProps} />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}

/* ================= TIÊU ĐỀ NHÓM LỚN (Đang hiển thị / Chưa hiển thị) ================= */

function SectionHeader({
    title,
    count,
    actionLabel,
    actionDisabled,
    onAction,
}: {
    title: string;
    count: number;
    actionLabel: string;
    actionDisabled?: boolean;
    onAction: () => void;
}) {
    return (
        <div className="flex items-center justify-between px-2 pt-1 pb-1.5">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                {title} <span className="font-normal text-gray-400">({count})</span>
            </span>
            <Button
                type="link"
                size="small"
                disabled={actionDisabled}
                onClick={onAction}
                className="!px-1 !h-auto !text-[12px] !text-[#3e91ab] disabled:!text-gray-300"
            >
                {actionLabel}
            </Button>
        </div>
    );
}

/* ================= NÚT + POPOVER ================= */

export default function ColumnSettingButton({
    items,
    hasCustomWidths,
    onToggle,
    onToggleAll,
    onMove,
    onFixedChange,
    onReset,
    onResetWidths,
    className,
}: ColumnSettingProps & { className?: string }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    /* ----- Hiệu ứng trượt (FLIP): chụp vị trí các dòng trước khi đổi, sau khi render
       cho từng dòng trượt từ chỗ cũ về chỗ mới; dòng vừa bật/tắt được tô sáng thoáng qua ----- */
    const rowEls = useRef(new Map<string, HTMLElement>());
    const snapshot = useRef<{ rects: Map<string, DOMRect>; changed: string[] } | null>(null);

    const registerRow = useCallback<RegisterRow>((key, el) => {
        if (el) rowEls.current.set(key, el);
        else rowEls.current.delete(key);
    }, []);

    const capture = (changed: string[]) => {
        const rects = new Map<string, DOMRect>();
        rowEls.current.forEach((el, key) => rects.set(key, el.getBoundingClientRect()));
        snapshot.current = { rects, changed };
    };

    useLayoutEffect(() => {
        const snap = snapshot.current;
        if (!snap) return;
        snapshot.current = null;
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        rowEls.current.forEach((el, key) => {
            if (typeof el.animate !== "function") return;
            const before = snap.rects.get(key);
            const after = el.getBoundingClientRect();
            const dy = before ? before.top - after.top : 0;
            if (!reduceMotion && Math.abs(dy) > 1) {
                el.animate(
                    [{ transform: `translateY(${dy}px)` }, { transform: "translateY(0)" }],
                    { duration: 220, easing: "cubic-bezier(0.2, 0, 0, 1)" }
                );
            }
            if (snap.changed.includes(key)) {
                el.animate(
                    [{ backgroundColor: "rgba(62, 145, 171, 0.22)" }, { backgroundColor: "rgba(62, 145, 171, 0)" }],
                    { duration: 900, easing: "ease-out" }
                );
            }
        });
    }, [items]);

    const handleToggle = (key: string) => {
        capture([key]);
        onToggle(key);
    };

    const handleShowAll = () => {
        capture(items.filter((i) => !i.visible).map((i) => i.key));
        onToggleAll(true);
    };

    const handleHideAll = () => {
        capture(items.filter((i) => i.visible && !i.locked).map((i) => i.key));
        onToggleAll(false);
    };

    /* ----- Chia nhóm ----- */
    const q = normalize(search.trim());
    const matches = (i: ColumnSettingItem) => !q || normalize(labelText(i)).includes(q);

    const visibleAll = items.filter((i) => i.visible);
    const hiddenAll = items.filter((i) => !i.visible);
    const visibleItems = visibleAll.filter(matches);
    const hiddenItems = hiddenAll.filter(matches).sort((a, b) => a.defaultIndex - b.defaultIndex);

    const canHideCount = visibleAll.filter((i) => !i.locked).length;
    // Luôn giữ ít nhất 1 cột: không có cột khoá thì còn 1 cột là hết ẩn được
    const hideAllDisabled = canHideCount === 0 || (visibleAll.length === 1 && !visibleAll[0].locked);

    const fixedGroups: { title: string; items: ColumnSettingItem[] }[] = [
        { title: t("table_setting.fixed_left"), items: visibleItems.filter((i) => i.fixed === "left") },
        { title: t("table_setting.unfixed"), items: visibleItems.filter((i) => !i.fixed) },
        { title: t("table_setting.fixed_right"), items: visibleItems.filter((i) => i.fixed === "right") },
    ];

    const noMatch = !!q && !visibleItems.length && !hiddenItems.length;

    const content = (
        <div className="w-[340px] max-w-[calc(100vw-32px)]">
            {/* HEADER */}
            <div className="flex items-center justify-between px-4 h-12 border-b border-gray-200 dark:border-[#303030]">
                <span className="font-semibold text-[14px] text-000000e0 dark:text-white">
                    {t("table_setting.title")}
                </span>
                <Button type="link" size="small" onClick={onReset} className="!px-1 !text-[#3e91ab]">
                    {t("table_setting.reset")}
                </Button>
            </div>

            {/* TÌM CỘT */}
            <div className="px-3 pt-3">
                <Input
                    allowClear
                    size="middle"
                    prefix={<SearchOutlined className="text-gray-400" />}
                    placeholder={t("table_setting.search_placeholder")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* DANH SÁCH CỘT */}
            <div className="overflow-y-auto px-2 py-2" style={{ maxHeight: "min(520px, max(220px, calc(100vh - 420px)))" }}>
                {noMatch ? (
                    <div className="py-6 text-center text-[13px] text-gray-400">{t("table_setting.no_match")}</div>
                ) : (
                    <>
                        {/* ĐANG HIỂN THỊ */}
                        {(!q || visibleItems.length > 0) && (
                            <div className="rounded-lg bg-gray-50 dark:bg-[#171717] p-1 mb-3">
                                <SectionHeader
                                    title={t("table_setting.visible_section")}
                                    count={visibleAll.length}
                                    actionLabel={t("table_setting.hide_all")}
                                    // Đang lọc: nút tác động mọi cột chứ không riêng cột đang thấy -> khoá
                                    actionDisabled={hideAllDisabled || !!q}
                                    onAction={handleHideAll}
                                />
                                {fixedGroups.map((g) => (
                                    <ColumnGroup
                                        key={g.title}
                                        title={g.title}
                                        items={g.items}
                                        // Đang lọc thì danh sách không liền mạch -> tạm khoá kéo thả
                                        dragDisabled={!!q}
                                        onMove={onMove}
                                        onToggle={handleToggle}
                                        onFixedChange={onFixedChange}
                                        registerRow={registerRow}
                                    />
                                ))}
                            </div>
                        )}

                        {/* CHƯA HIỂN THỊ */}
                        {hiddenItems.length > 0 && (
                            <div className="p-1">
                                <SectionHeader
                                    title={t("table_setting.hidden_section")}
                                    count={hiddenAll.length}
                                    actionLabel={t("table_setting.show_all")}
                                    actionDisabled={!!q}
                                    onAction={handleShowAll}
                                />
                                {hiddenItems.map((item) => (
                                    <HiddenRow key={item.key} item={item} onToggle={handleToggle} registerRow={registerRow} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* FOOTER: độ rộng cột */}
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-gray-200 dark:border-[#303030] text-[12px] text-gray-500 dark:text-gray-400">
                <span>{t("table_setting.resize_hint")}</span>
                {hasCustomWidths && (
                    <Button type="link" size="small" onClick={onResetWidths} className="!px-1 !text-[12px] !text-[#3e91ab] shrink-0">
                        {t("table_setting.reset_widths")}
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <Popover
            open={open}
            onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}
            trigger="click"
            placement="bottomRight"
            content={content}
            arrow={false}
            overlayClassName="column-setting-popover"
            destroyOnHidden
        >
            <Tooltip title={t("table_setting.tooltip")} open={open ? false : undefined}>
                <Button
                    icon={<SettingOutlined />}
                    className={`shrink-0 dark:!bg-[#1f1f1f] dark:!border-[#303030] dark:!text-gray-200 ${className || ""}`}
                    aria-label={t("table_setting.tooltip")}
                />
            </Tooltip>
        </Popover>
    );
}
